import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProfessionalHeader from '../components/ProfessionalHeader';
import { getMyProfiles, getMyProfileCount, UserProfileSummary } from '../services/recommendationService';
import toast from 'react-hot-toast';

const UserDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<UserProfileSummary[]>([]);
  const [totalFittings, setTotalFittings] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load both count and profiles in parallel
      const [countData, profilesData] = await Promise.all([
        getMyProfileCount(),
        getMyProfiles(0, 10) // Get last 10 fittings
      ]);

      setTotalFittings(countData.total_fittings);
      setProfiles(profilesData);
    } catch (err: any) {
      console.error('Error loading dashboard:', err);
      setError(err.message || 'Failed to load your fitting history');
      toast.error('Failed to load fitting history');
    } finally {
      setLoading(false);
    }
  };

  const handleStartNewFitting = () => {
    navigate('/advanced');
  };

  const handleViewProfile = (profile: UserProfileSummary) => {
    navigate(`/fitting/${profile.profile_id}`);
  };

  const handlePostFittingReview = (profileId: number) => {
    navigate(`/review/${profileId}`);
  };

  const formatDate = (dateString: string) => {
    // API returns UTC datetimes without a timezone suffix (e.g. "2026-02-14T22:34:32").
    // Append 'Z' so the browser treats it as UTC and toLocaleDateString converts to local time.
    const normalized = dateString.replace(' ', 'T');
    const utcString = normalized.endsWith('Z') ? normalized : normalized + 'Z';
    const date = new Date(utcString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfessionalHeader user={user} onLogout={logout} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-ink mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading your fitting history...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="mb-4 text-green-ink hover:text-green-ink font-medium flex items-center"
          >
            ← Back to Home
          </button>
          <h1 className="text-3xl font-bold text-gray-900">My Fitting History</h1>
          <p className="mt-2 text-gray-600">
            View your past fittings and track your equipment journey
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Total Fittings
            </div>
            <div className="mt-2 text-3xl font-bold text-green-ink">{totalFittings}</div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
              Latest Fitting
            </div>
            <div className="mt-2 text-lg font-semibold text-gray-900">
              {profiles.length > 0
                ? formatDate(profiles[0].created_at)
                : 'No fittings yet'}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 flex items-center justify-center">
            <button
              onClick={handleStartNewFitting}
              className="w-full px-6 py-3 bg-green-ink text-white rounded-lg hover:opacity-90 font-semibold transition-colors"
            >
              + Start New Fitting
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <button
              onClick={loadDashboardData}
              className="mt-2 text-red-600 hover:text-red-700 font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Fitting History Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Recent Fittings</h2>
          </div>

          {profiles.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No fittings yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first club fitting profile.
              </p>
              <div className="mt-6">
                <button
                  onClick={handleStartNewFitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-ink hover:opacity-90"
                >
                  Start Your First Fitting
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Handicap
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Swing Speed
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {profiles.map((profile) => (
                    <tr key={profile.profile_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(profile.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {profile.handicap !== null ? profile.handicap : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {profile.swing_speed_driver !== null
                          ? `${profile.swing_speed_driver} mph`
                          : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                        <button
                          onClick={() => handleViewProfile(profile)}
                          className="text-green-ink hover:text-green-ink"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handlePostFittingReview(profile.profile_id)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Post-Fitting Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Load More Button (if needed) */}
        {profiles.length >= 10 && (
          <div className="mt-6 text-center">
            <button
              onClick={() => toast('Load more feature coming soon!')}
              className="px-4 py-2 text-green-ink hover:text-green-ink font-medium"
            >
              Load More Fittings
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default UserDashboard;
